import { z } from 'zod';

/**
 * Single source of truth for the account-write password policy (signup,
 * employee invite/accept, password reset). Shared so the backend Zod schema and
 * the frontend live-requirements checklist can never drift apart.
 *
 * NOT applied to login — existing users may hold older, weaker passwords.
 */
export const PASSWORD_MIN_CHAR = 8;

export const PASSWORD_RULES: { label: string; test: (v: string) => boolean }[] =
  [
    {
      label: `At least ${PASSWORD_MIN_CHAR} characters`,
      test: v => v.length >= PASSWORD_MIN_CHAR,
    },
    { label: 'One lowercase letter', test: v => /[a-z]/.test(v) },
    { label: 'One uppercase letter', test: v => /[A-Z]/.test(v) },
    { label: 'One number', test: v => /[0-9]/.test(v) },
  ];

export const isPasswordValid = (value: string): boolean =>
  PASSWORD_RULES.every(rule => rule.test(value));

export const passwordSchema = z
  .string({ required_error: 'Password is required' })
  .min(
    PASSWORD_MIN_CHAR,
    `Password must be at least ${PASSWORD_MIN_CHAR} characters long`,
  )
  .regex(/[a-z]/, 'Password must contain a lowercase letter')
  .regex(/[A-Z]/, 'Password must contain an uppercase letter')
  .regex(/[0-9]/, 'Password must contain a number');
